import { Check } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { isApiError, type Versioned } from '../api/client';
import { useApi } from '../api/context';
import {
  useAllCloudAccounts,
  useBlueprint,
  useProviders,
  useResourceTypeMap,
  useSecretStores,
} from '../api/queries';
import type {
  BlueprintDocument,
  CloudAccount,
  Deployment,
  Diagnostic,
  Provider,
  SecretStore,
} from '../api/types';
import { usePermission } from '../auth/context';
import { useToast } from '../components/toast';
import { Button, Dialog, DiagnosticList, ErrorMessage, Field, Loading } from '../components/ui';
import { describedBy } from '../lib/a11y';
import { strings } from '../strings';
import { diagnosticTarget, dryRunProviders, useDryRun, type CreateStep } from './dryRun';
import {
  AccountFields,
  FailurePolicyField,
  OverridesTable,
  ParameterField,
  RegionSelect,
  SecretStoreField,
  VaultFields,
  VaultModeField,
} from './fields';
import {
  checkAccounts,
  checkName,
  checkParameters,
  fromForm,
  toForm,
  usedProviders,
  type FormErrors,
  limitationMessage,
  type SettingsForm,
} from './settingsForm';

const c = strings.createDeployment;
const STEPS: readonly CreateStep[] = [1, 2, 3, 4];

/** Create a deployment in four steps (spec 6.4 Create, design 8.2). */
export function CreateDeploymentDialog({
  blueprintId,
  onClose,
  onCreated,
}: {
  blueprintId: string;
  onClose: () => void;
  onCreated: (deployment: Versioned<Deployment>) => void;
}) {
  const blueprint = useBlueprint(blueprintId);
  const providers = useProviders();
  const operate = usePermission('operate');
  const accounts = useAllCloudAccounts(operate);
  const stores = useSecretStores(operate);
  const error = blueprint.error ?? providers.error ?? accounts.error;
  if (error || !blueprint.data || !providers.data || !accounts.data) {
    return (
      <Dialog title={c.title} onClose={onClose}>
        {error ? <ErrorMessage error={error} /> : <Loading />}
      </Dialog>
    );
  }
  return (
    <CreateSteps
      blueprintId={blueprintId}
      doc={blueprint.data.data.content}
      providers={providers.data}
      accounts={accounts.data}
      stores={stores.data ?? []}
      onClose={onClose}
      onCreated={onCreated}
    />
  );
}

function CreateSteps({
  blueprintId,
  doc,
  providers,
  accounts,
  stores,
  onClose,
  onCreated,
}: {
  blueprintId: string;
  doc: BlueprintDocument;
  providers: Provider[];
  accounts: CloudAccount[];
  stores: SecretStore[];
  onClose: () => void;
  onCreated: (deployment: Versioned<Deployment>) => void;
}) {
  const api = useApi();
  const toast = useToast();
  const admin = usePermission('admin');
  const types = useResourceTypeMap();
  const ids = useId();
  const definitions = useMemo(() => doc.parameters ?? {}, [doc.parameters]);
  const [step, setStep] = useState<CreateStep>(1);
  const [form, setForm] = useState<SettingsForm>(() => toForm('', undefined, definitions));
  const [checked, setChecked] = useState<ReadonlySet<CreateStep>>(new Set());
  const [diagnostics, setDiagnostics] = useState<Diagnostic[]>([]);
  const [busy, setBusy] = useState(false);
  const dry = useDryRun(blueprintId);
  const used = dryRunProviders(dry.data) ?? usedProviders(form, doc);
  const update = (patch: Partial<SettingsForm>) => setForm((f) => ({ ...f, ...patch }));
  const providerOf = (key: string) => providers.find((p) => p.key === key);
  const request = () => ({ name: form.name, settings: fromForm(form, doc, definitions, { used }) });

  const browserErrors = (s: CreateStep): FormErrors => {
    if (s === 1) return checkName(form);
    if (s === 2) return checkAccounts(form, doc, used);
    if (s === 3) return checkParameters(form, definitions);
    return {};
  };
  const shownBrowser: FormErrors = Object.assign(
    {},
    ...[...checked].map((s) => browserErrors(s)),
  ) as FormErrors;
  const visible = diagnostics.filter((d) => checked.has(diagnosticTarget(d).step));
  const errorsAt = (pointer: string) => [
    ...(shownBrowser[pointer] ?? []),
    ...visible.filter((d) => diagnosticTarget(d).field === pointer).map((d) => d.message),
  ];
  const general = visible.filter(
    (d) => diagnosticTarget(d).step === step && diagnosticTarget(d).field === null,
  );
  const stepHasErrors = (s: CreateStep) =>
    Object.keys(browserErrors(s)).length > 0 ||
    diagnostics.some((d) => d.severity === 'error' && diagnosticTarget(d).step === s);

  const next = async () => {
    setChecked((current) => new Set([...current, step]));
    if (Object.keys(browserErrors(step)).length > 0) return;
    const result = await dry.run(request());
    if (result) {
      setDiagnostics(result.diagnostics);
      const blocking = result.diagnostics
        .filter((d) => d.severity === 'error')
        .map((d) => diagnosticTarget(d).step)
        .filter((s) => s <= step);
      if (blocking.length) {
        setStep(Math.min(...blocking) as CreateStep);
        return;
      }
    }
    setStep((step + 1) as CreateStep);
  };

  const create = async () => {
    setChecked(new Set(STEPS));
    const invalid = STEPS.find((s) => Object.keys(browserErrors(s)).length > 0);
    if (invalid) {
      setStep(invalid);
      return;
    }
    setBusy(true);
    try {
      // Feature spec 15 9.8: create sends `vaultMode` explicitly (the dry run's default when the form holds none).
      const body = request();
      if (!body.settings.vaultMode && dry.data?.settings.vaultMode)
        body.settings.vaultMode = dry.data.settings.vaultMode;
      onCreated(await api.createDeployment(blueprintId, body));
    } catch (error) {
      if (isApiError(error) && error.diagnostics.length > 0) {
        setDiagnostics(error.diagnostics);
        const first = Math.min(...error.diagnostics.map((d) => diagnosticTarget(d).step)) as CreateStep;
        setStep(first);
      } else {
        toast.error(error);
      }
    } finally {
      setBusy(false);
    }
  };

  const vault = dry.data?.vault ?? null;
  const appRegions = vault
    ? [
        ...new Set(
          (dry.data?.resources ?? [])
            .filter((r) => types.get(r.type)?.injectionProperty && r.region && r.region !== vault.region)
            .map((r) => r.region!),
        ),
      ]
    : [];
  const id = (suffix: string) => `${ids}-${suffix}`;

  return (
    <Dialog
      title={c.title}
      onClose={onClose}
      wide
      footer={
        <>
          {dry.pending && (
            <span role="status" className="mr-auto self-center text-sm text-fg-muted">
              {c.checking}
            </span>
          )}
          <Button onClick={onClose}>{strings.common.cancel}</Button>
          {step > 1 && <Button onClick={() => setStep((step - 1) as CreateStep)}>{c.back}</Button>}
          {step < 4 ? (
            <Button variant="primary" disabled={dry.pending} onClick={() => void next()}>
              {c.next}
            </Button>
          ) : (
            <Button variant="primary" disabled={busy} onClick={() => void create()}>
              {c.create}
            </Button>
          )}
        </>
      }
    >
      <ol aria-label={c.steps} className="stepper">
        {STEPS.map((s) => {
          const name = c.stepNames[s - 1];
          const done = s < step;
          const content = (
            <>
              <span className="stepper-number" aria-hidden>
                {done ? <Check className="size-3.5" /> : s}
              </span>
              <span>{name}</span>
            </>
          );
          return (
            <li
              key={s}
              aria-current={s === step ? 'step' : undefined}
              className={done ? 'is-done' : undefined}
            >
              {done ? (
                <button type="button" className="stepper-item" onClick={() => setStep(s)}>
                  {content}
                </button>
              ) : (
                <span className="stepper-item">{content}</span>
              )}
            </li>
          );
        })}
      </ol>

      <form
        noValidate
        aria-label={c.step(step, c.stepNames[step - 1])}
        onSubmit={(event) => {
          event.preventDefault();
          if (step < 4) void next();
          else void create();
        }}
      >
        <h3 className="mb-3 text-base font-semibold">{c.stepNames[step - 1]}</h3>
        {checked.has(step) && stepHasErrors(step) && (
          <p role="alert" className="field-error mb-3 text-sm">
            {c.stepErrors}
          </p>
        )}
        {general.length > 0 && (
          <div className="mb-3">
            <p className="mb-1 text-sm">{c.otherProblems}</p>
            <DiagnosticList diagnostics={general} />
          </div>
        )}

        {step === 1 && (
          <>
            <Field
              label={strings.settings.name}
              htmlFor={id('name')}
              help={strings.settings.nameHelp}
              errors={errorsAt('/name')}
              required
            >
              <input
                id={id('name')}
                className="input"
                value={form.name}
                maxLength={64}
                autoComplete="off"
                aria-invalid={errorsAt('/name').length ? true : undefined}
                aria-describedby={describedBy(id('name'), true, errorsAt('/name').length > 0)}
                onChange={(e) => update({ name: e.target.value })}
              />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={strings.settings.provider}
                htmlFor={id('provider')}
                errors={errorsAt('/settings/defaultPlacement/provider')}
              >
                <select
                  id={id('provider')}
                  className="input"
                  value={form.defaultProvider}
                  onChange={(e) => update({ defaultProvider: e.target.value, defaultRegion: '' })}
                >
                  <option value="">{strings.settings.noProvider}</option>
                  {providers.map((p) => (
                    <option key={p.key} value={p.key}>
                      {p.displayName}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label={strings.settings.region}
                htmlFor={id('region')}
                errors={errorsAt('/settings/defaultPlacement/region')}
              >
                <RegionSelect
                  id={id('region')}
                  provider={providerOf(form.defaultProvider)}
                  value={form.defaultRegion}
                  onChange={(value) => update({ defaultRegion: value })}
                />
              </Field>
            </div>
          </>
        )}

        {step === 2 && (
          <AccountFields
            idPrefix={ids}
            providers={providers}
            used={used}
            accounts={accounts}
            form={form}
            setForm={setForm}
            errorsAt={errorsAt}
            admin={admin}
          />
        )}

        {step === 3 &&
          (Object.keys(definitions).length === 0 ? (
            <p className="help">{strings.settings.noParameters}</p>
          ) : (
            <div className="grid gap-x-3 sm:grid-cols-2">
              {Object.entries(definitions).map(([name, definition]) => (
                <ParameterField
                  key={name}
                  id={id(`param-${name}`)}
                  name={name}
                  definition={definition}
                  value={form.parameters[name] ?? ''}
                  errors={errorsAt(`/settings/parameters/${name}`)}
                  onChange={(value) =>
                    setForm((f) => ({ ...f, parameters: { ...f.parameters, [name]: value } }))
                  }
                />
              ))}
            </div>
          ))}

        {step === 4 && (
          <>
            <VaultModeField
              idPrefix={ids}
              form={form}
              update={update}
              providers={providers}
              effective={dry.data?.settings.vaultMode ?? null}
              limitation={limitationMessage(
                providerOf(
                  form.vaultPlacementChoice === 'custom' && form.vaultProvider
                    ? form.vaultProvider
                    : form.defaultProvider,
                ),
                doc,
              )}
              errors={errorsAt('/settings/vaultMode')}
            />
            <VaultFields
              idPrefix={ids}
              form={form}
              update={update}
              providers={providers}
              errorsAt={errorsAt}
              appsElsewhere={
                vault && appRegions.length ? c.appsElsewhere(appRegions.join(', '), vault.region) : null
              }
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <SecretStoreField
                id={id('store')}
                value={form.secretStore}
                stores={stores}
                onChange={(value) => update({ secretStore: value })}
                errors={errorsAt('/settings/secretStore')}
              />
              <FailurePolicyField
                name={id('onFailure')}
                value={form.onFailure}
                onChange={(value) => update({ onFailure: value })}
                errors={errorsAt('/settings/onFailure')}
              />
            </div>
            <details
              className="mt-3"
              open={
                visible.some((d) => (d.path ?? '').startsWith('/settings/placementOverrides')) || undefined
              }
            >
              <summary className="cursor-pointer text-sm font-semibold">{c.advanced}</summary>
              <p className="help my-2">{strings.settings.placementOverridesHelp}</p>
              <OverridesTable
                idPrefix={ids}
                doc={doc}
                form={form}
                setForm={setForm}
                providers={providers}
                errorsAt={errorsAt}
              />
            </details>
          </>
        )}
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
